/**
 * Resolve admission-list / detail Q·A by referencing the original extract particle.
 * Snapshot fields are fallback only when the structural unit is missing.
 */
import type { PrivateActivityPackage } from '../../services/ttqService';
import { buildPapDetail } from '../../features/ttqib/papDisplay';

const INDEX_ONLY_RE = /^【\s*\d+\s*】\s*[:：]?$/u;
const MATHISH_RE = /[=＝]|[×÷+\-−*/()]|\$|\\times|\\div|\d/;

export function isIndexOnlyQuestionStem(text: string): boolean {
  const s = String(text || '').replace(/\s+/g, ' ').trim();
  if (!s) return true;
  return INDEX_ONLY_RE.test(s);
}

function looksLikeEquation(text: string): boolean {
  const s = String(text || '').replace(/\s+/g, ' ').trim();
  if (!s || s.length < 2) return false;
  if (!MATHISH_RE.test(s)) return false;
  if (/^(观察|利用|应用|拆分|展开|先算|再算|最后|得出|计算最终)/u.test(s) && !/[=＝×÷]/.test(s)) {
    return false;
  }
  return true;
}

/** Pull equation body from PAP 作用对象 like「算式125×(80+8)」/「两个乘法项22×48和88×13」. */
export function recoverEquationFromPap(pap: PrivateActivityPackage | Record<string, unknown> | null | undefined): string | null {
  if (!pap) return null;
  const detail = buildPapDetail(pap as PrivateActivityPackage);
  const objects: string[] = [];
  for (const step of detail.implicitSteps) {
    if (step.actionObject) objects.push(step.actionObject);
  }
  for (const step of detail.examPointSteps) {
    if (step.actionObject) objects.push(step.actionObject);
  }
  for (const raw of objects) {
    let cleaned = String(raw || '')
      .replace(/^(算式|原式|表达式|题目|式子)[:：\s]*/u, '')
      .trim();
    // Drop leading CJK prose before the first digit / math atom.
    cleaned = cleaned.replace(/^[\u4e00-\u9fff\s：:、，,]+(?=\d|\$)/u, '').trim();
    cleaned = cleaned.replace(/和(?=\d)/g, '+').replace(/与(?=\d)/g, '+');
    if (looksLikeEquation(cleaned)) return cleaned;
  }
  return null;
}

export function recoverEquationFromSubAnswers(subAnswers: string[] | null | undefined): string | null {
  for (const raw of subAnswers || []) {
    const s = String(raw || '').replace(/\s+/g, ' ').trim();
    const m = s.match(/原式\s*[=＝]\s*(.+)$/u);
    if (m?.[1] && looksLikeEquation(m[1])) return m[1].trim();
  }
  return null;
}

function indexPrefix(text: string): string {
  const m = String(text || '').match(/^【\s*\d+\s*】/);
  return m?.[0] || '';
}

function withRecoveredStem(
  main: string,
  opts: {
    pap?: PrivateActivityPackage | Record<string, unknown> | null;
    subAnswers?: string[] | null;
  },
): string {
  if (!isIndexOnlyQuestionStem(main)) return main;
  const recovered =
    recoverEquationFromPap(opts.pap)
    || recoverEquationFromSubAnswers(opts.subAnswers);
  if (!recovered) return main;
  const idx = indexPrefix(main);
  return [idx, recovered].filter(Boolean).join(' ').replace(/\s+/g, ' ').trim();
}

export type ResolvedAdmissionParticleQa = {
  question: string;
  answer: string;
  subQuestions: string[];
  subAnswers: string[];
  /** Where Q/A fields primarily came from. */
  source: 'extract' | 'snapshot';
};

/**
 * Always prefer live extract QA (same as「颗粒提取」). Snapshot is cache/fallback only.
 * When extract/snapshot main is only 【n】, recover equation from PAP / 子答案「原式=」。
 */
export function resolveAdmissionParticleQa(input: {
  particleQuestions?: string | null;
  particleAnswers?: string | null;
  particleTitle?: string | null;
  subQuestions?: string[] | null;
  subAnswers?: string[] | null;
  extractQuestion?: string | null;
  extractAnswer?: string | null;
  extractSubQuestions?: string[] | null;
  extractSubAnswers?: string[] | null;
  pap?: PrivateActivityPackage | Record<string, unknown> | null;
}): ResolvedAdmissionParticleQa {
  const extractQ = String(input.extractQuestion || '').trim();
  const extractA = String(input.extractAnswer || '').trim();
  const hasExtract =
    (extractQ && extractQ !== '（无题干）')
    || (extractA && extractA !== '（无答案）')
    || Boolean(input.extractSubQuestions?.length)
    || Boolean(input.extractSubAnswers?.length);

  const source: 'extract' | 'snapshot' = hasExtract ? 'extract' : 'snapshot';

  let question = hasExtract && extractQ && extractQ !== '（无题干）'
    ? extractQ
    : String(input.particleQuestions || input.particleTitle || '').trim();
  let answer = hasExtract && extractA && extractA !== '（无答案）'
    ? extractA
    : String(input.particleAnswers || '').trim();

  const extractSubs = (input.extractSubQuestions || []).map((s) => String(s || '').trim()).filter(Boolean);
  const snapSubs = (input.subQuestions || []).map((s) => String(s || '').trim()).filter(Boolean);
  const extractSubAns = (input.extractSubAnswers || []).map((s) => String(s || '').trim()).filter(Boolean);
  const snapSubAns = (input.subAnswers || []).map((s) => String(s || '').trim()).filter(Boolean);

  const subQuestions = hasExtract ? (extractSubs.length ? extractSubs : snapSubs) : snapSubs;
  const subAnswers = hasExtract ? (extractSubAns.length ? extractSubAns : snapSubAns) : snapSubAns;

  question = withRecoveredStem(question, {
    pap: input.pap,
    subAnswers,
  });

  return {
    question,
    answer,
    subQuestions,
    subAnswers,
    source,
  };
}

export function admissionParticleListLabel(
  qa: ResolvedAdmissionParticleQa,
  includeSubsInline = true,
): string {
  const main = qa.question;
  if (!includeSubsInline || !qa.subQuestions.length) return main;
  const collapsed = main.replace(/\s+/g, ' ');
  const missing = qa.subQuestions.filter((s) => {
    const needle = s.replace(/\s+/g, ' ').trim();
    return Boolean(needle) && !collapsed.includes(needle);
  });
  if (!missing.length) return main;
  const block = missing
    .map((s) => {
      const ordinal = qa.subQuestions.indexOf(s);
      return `（${(ordinal >= 0 ? ordinal : 0) + 1}）${s}`;
    })
    .join(' ');
  return `${main} ${block}`.trim();
}
